'use client'
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Mail, Loader2 } from 'lucide-react';

import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input";
import { shadcnToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeInternalPath } from "@/utils/url";
import type { User } from '@supabase/supabase-js';

export interface SignInCardProps {
  /** Hides footer, external links, help menu - for embedding in other flows */
  embedded?: boolean;
  /** Prevents navigating to /dashboard after auth success */
  disableRedirect?: boolean;
  /** Callback for when auth succeeds - useful for onboarding flows */
  onAuthSuccess?: (user: User) => void;
}

export function SignInCard({
  embedded = false,
  disableRedirect = false,
  onAuthSuccess
}: SignInCardProps = {}) {
  const navigate = useNavigate();
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Track magic link request generation to prevent stale state updates
  const magicLinkRequestIdRef = useRef(0);

  // Listen for auth state changes (for OAuth and magic link flows)
  // Also check for existing session on mount (for redirect-back scenarios)
  useEffect(() => {
    if (!onAuthSuccess) return;
    
    // Check if user is already authenticated (for redirect-back scenarios)
    const checkExistingAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        onAuthSuccess(user);
      }
    };
    checkExistingAuth();
    
    // Also listen for future auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle both SIGNED_IN and INITIAL_SESSION events
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        onAuthSuccess(session.user);
      }
    });
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [onAuthSuccess]);

  // For 3D card effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [10, -10]);
  const rotateY = useTransform(mouseX, [-300, 300], [-10, 10]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Derive a safe redirectTo (preserve next when present)
  const makeRedirectTo = () => {
    const params = new URLSearchParams(window.location.search);
    const safeNext = sanitizeInternalPath(params.get('next'));
    return `${window.location.origin}/auth/callback${safeNext ? `?next=${encodeURIComponent(safeNext)}` : ''}`;
  };

  return (
    <div
      className={cn(
        "w-full relative flex items-center justify-center",
        embedded
          ? "bg-transparent min-h-[600px]"
          : "bg-black min-h-screen overflow-y-auto overflow-x-hidden"
      )}
    >
      {/* In embedded mode (e.g. onboarding step), avoid rendering the full-screen backdrop.
          It looks "pasted" when nested inside another Card. */}
      {!embedded && (
        <>
          {/* Background gradient effect - Now #5956F6 to white */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#5956F6]/40 via-[#5956F6]/50 to-white" />

          {/* Subtle noise texture overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] mix-blend-soft-light"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundSize: '200px 200px'
            }}
          />

          {/* Top radial glow - Now #5956F6 */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[120vh] h-[60vh] rounded-b-[50%] bg-[#5956F6]/20 blur-[80px]" />
          <motion.div
            className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[100vh] h-[60vh] rounded-b-full bg-[#5956F6]/20 blur-[60px]"
            animate={{
              opacity: [0.15, 0.3, 0.15],
              scale: [0.98, 1.02, 0.98]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: "mirror"
            }}
          />
          <motion.div
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[90vh] h-[90vh] rounded-t-full bg-[#5956F6]/20 blur-[60px]"
            animate={{
              opacity: [0.3, 0.5, 0.3],
              scale: [1, 1.1, 1]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              repeatType: "mirror",
              delay: 1
            }}
          />

          {/* Animated glow spots */}
          <div className="absolute left-1/4 top-1/4 w-96 h-96 bg-white/5 rounded-full blur-[100px] animate-pulse opacity-40" />
          <div className="absolute right-1/4 bottom-1/4 w-96 h-96 bg-white/5 rounded-full blur-[100px] animate-pulse delay-1000 opacity-40" />
        </>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-sm relative z-10"
        style={{ perspective: 1500 }}
      >
        <motion.div
          className="relative"
          style={{ rotateX, rotateY }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          whileHover={{ z: 10 }}
        >
          <div className="relative group">
            {/* Card glow effect */}
            <motion.div 
              className="absolute -inset-[1px] rounded-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"
              animate={{
                boxShadow: [
                  "0 0 10px 2px rgba(255,255,255,0.03)",
                  "0 0 15px 5px rgba(255,255,255,0.05)",
                  "0 0 10px 2px rgba(255,255,255,0.03)"
                ],
                opacity: [0.2, 0.4, 0.2]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut", 
                repeatType: "mirror" 
              }}
            />

            {/* Traveling light beam effect */}
            <div className="absolute -inset-[1px] rounded-2xl overflow-hidden">
              {/* Top light beam */}
              <motion.div 
                className="absolute top-0 left-0 h-[3px] w-[50%] bg-gradient-to-r from-transparent via-white to-transparent opacity-70"
                initial={{ filter: "blur(2px)" }}
                animate={{ 
                  left: ["-50%", "100%"],
                  opacity: [0.3, 0.7, 0.3],
                  filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"]
                }}
                transition={{ 
                  left: {
                    duration: 2.5, 
                    ease: "easeInOut", 
                    repeat: Infinity,
                    repeatDelay: 1
                  },
                  opacity: {
                    duration: 1.2,
                    repeat: Infinity,
                    repeatType: "mirror"
                  },
                  filter: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: "mirror"
                  }
                }}
              />
              
              {/* Right, Bottom, and Left light beams */}
              <motion.div 
                className="absolute top-0 right-0 h-[50%] w-[3px] bg-gradient-to-b from-transparent via-white to-transparent opacity-70"
                initial={{ filter: "blur(2px)" }}
                animate={{ 
                  top: ["-50%", "100%"],
                  opacity: [0.3, 0.7, 0.3],
                  filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"]
                }}
                transition={{ 
                  top: {
                    duration: 2.5, 
                    ease: "easeInOut", 
                    repeat: Infinity,
                    repeatDelay: 1,
                    delay: 0.6
                  },
                  opacity: {
                    duration: 1.2,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 0.6
                  },
                  filter: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 0.6
                  }
                }}
              />
              
              <motion.div 
                className="absolute bottom-0 right-0 h-[3px] w-[50%] bg-gradient-to-r from-transparent via-white to-transparent opacity-70"
                initial={{ filter: "blur(2px)" }}
                animate={{ 
                  right: ["-50%", "100%"],
                  opacity: [0.3, 0.7, 0.3],
                  filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"]
                }}
                transition={{ 
                  right: {
                    duration: 2.5, 
                    ease: "easeInOut", 
                    repeat: Infinity,
                    repeatDelay: 1,
                    delay: 1.2
                  },
                  opacity: {
                    duration: 1.2,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 1.2
                  },
                  filter: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 1.2
                  }
                }}
              />
              
              <motion.div 
                className="absolute bottom-0 left-0 h-[50%] w-[3px] bg-gradient-to-b from-transparent via-white to-transparent opacity-70"
                initial={{ filter: "blur(2px)" }}
                animate={{ 
                  bottom: ["-50%", "100%"],
                  opacity: [0.3, 0.7, 0.3],
                  filter: ["blur(1px)", "blur(2.5px)", "blur(1px)"]
                }}
                transition={{ 
                  bottom: {
                    duration: 2.5, 
                    ease: "easeInOut", 
                    repeat: Infinity,
                    repeatDelay: 1,
                    delay: 1.8
                  },
                  opacity: {
                    duration: 1.2,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 1.8
                  },
                  filter: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatType: "mirror",
                    delay: 1.8
                  }
                }}
              />
              
              {/* Corner glow spots */}
              <motion.div 
                className="absolute top-0 left-0 h-[5px] w-[5px] rounded-full bg-white/40 blur-[1px]"
                animate={{ 
                  opacity: [0.2, 0.4, 0.2] 
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity,
                  repeatType: "mirror"
                }}
              />
              <motion.div 
                className="absolute top-0 right-0 h-[8px] w-[8px] rounded-full bg-white/60 blur-[2px]"
                animate={{ 
                  opacity: [0.2, 0.4, 0.2] 
                }}
                transition={{ 
                  duration: 2.4, 
                  repeat: Infinity,
                  repeatType: "mirror",
                  delay: 0.5
                }}
              />
              <motion.div 
                className="absolute bottom-0 right-0 h-[8px] w-[8px] rounded-full bg-white/60 blur-[2px]"
                animate={{ 
                  opacity: [0.2, 0.4, 0.2] 
                }}
                transition={{ 
                  duration: 2.2, 
                  repeat: Infinity,
                  repeatType: "mirror",
                  delay: 1
                }}
              />
              <motion.div 
                className="absolute bottom-0 left-0 h-[5px] w-[5px] rounded-full bg-white/40 blur-[1px]"
                animate={{ 
                  opacity: [0.2, 0.4, 0.2] 
                }}
                transition={{ 
                  duration: 2.3, 
                  repeat: Infinity,
                  repeatType: "mirror",
                  delay: 1.5
                }}
              />
            </div>

            {/* Card border glow */}
            <div className="absolute -inset-[0.5px] rounded-2xl bg-gradient-to-r from-white/3 via-white/7 to-white/3 opacity-0 group-hover:opacity-70 transition-opacity duration-500" />
            
            {/* Glass card background */}
            <div className="relative bg-black/40 backdrop-blur-xl rounded-2xl p-6 border border-white/[0.05] shadow-2xl overflow-hidden">
              {/* Subtle card inner patterns */}
              <div className="absolute inset-0 opacity-[0.03]" 
                style={{
                  backgroundImage: `linear-gradient(135deg, white 0.5px, transparent 0.5px), linear-gradient(45deg, white 0.5px, transparent 0.5px)`,
                  backgroundSize: '30px 30px'
                }}
              />

              {/* Logo and header */}
              <div className="text-center space-y-1 mb-5">
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", duration: 0.8 }}
                  className="mx-auto w-10 h-10 rounded-full border border-white/10 flex items-center justify-center relative overflow-hidden"
                >
                  <img src="/uploads/white-chair.png" alt="White Chair Logo" className="h-8 w-auto" />
                  {/* Inner lighting effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80"
                >
                  Welcome to Figgy
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-white/60 text-xs"
                >
                  Sign in or up to continue
                </motion.p>
              </div>

              {/* Auth options */}
              <div className="space-y-4">
                {/* Google SSO and Magic Link */}
                <div className="space-y-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="w-full relative group/google"
                    disabled={googleLoading}
                    onClick={async () => {
                      setGoogleLoading(true);
                      try {
                        // Store intended redirect - use current path if embedded, else dashboard
                        const redirectPath = embedded ? window.location.pathname : '/dashboard';
                        localStorage.setItem('authRedirectUrl', redirectPath);
                        await supabase.auth.signInWithOAuth({
                          provider: 'google',
                          options: { redirectTo: `${window.location.origin}/auth/callback` }
                        });
                        // Set a backup timeout to reset loading state if redirect doesn't happen
                        setTimeout(() => {
                          setGoogleLoading(false);
                        }, 15000); // Reset after 15 seconds if no redirect occurs
                        
                      } catch (error: any) {
                        shadcnToast({ description: error.message || "Failed to sign in with Google. Please check if popup blockers are enabled.", variant: "destructive" });
                        setGoogleLoading(false);
                      }
                    }}
                  >
                    <div className="absolute inset-0 bg-white/5 rounded-lg blur opacity-0 group-hover/google:opacity-70 transition-opacity duration-300" />
                    <div className="relative overflow-hidden bg-white/5 text-white font-medium h-10 rounded-lg border border-white/10 hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-2">
                      {googleLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          <path d="M1 1h22v22H1z" fill="none" />
                        </svg>
                      )}
                      <span className="text-white/80 group-hover/google:text-white transition-colors text-xs">
                        Sign in with Google
                      </span>
                    </div>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="w-full relative group/magic"
                    onClick={() => {
                      setShowMagicLink((v) => !v);
                      // Reset all feedback states when toggling form visibility
                      // (including loading state to prevent stale UI if form is closed during request)
                      setMagicSent(false);
                      setMagicError(null);
                      setMagicLoading(false);
                      // Invalidate any in-flight requests to prevent stale state updates
                      magicLinkRequestIdRef.current++;
                      // Note: Email field persists for UX convenience - user can retry without re-typing
                    }}
                  >
                    <div className="absolute inset-0 bg-white/5 rounded-lg blur opacity-0 group-hover/magic:opacity-70 transition-opacity duration-300" />
                    <div className="relative overflow-hidden bg-white/5 text-white font-medium h-10 rounded-lg border border-white/10 hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-2">
                      <Mail className="w-4 h-4" />
                      <span className="text-white/80 group-hover/magic:text-white transition-colors text-xs">
                        {showMagicLink ? '← Back' : 'Sign in with Magic Link'}
                      </span>
                    </div>
                  </motion.button>
                  {showMagicLink && (
                    <form
                      className="space-y-2 mt-2"
                      onSubmit={async (e) => {
                        e.preventDefault();

                        // Guard against rapid clicks/double submits before React re-renders
                        if (magicLoading) return;

                        if (!magicEmail) {
                          setMagicError("Please enter your email address");
                          return;
                        }

                        // Capture request ID to detect if this request was invalidated
                        const requestId = magicLinkRequestIdRef.current;

                        setMagicLoading(true);
                        setMagicError(null);
                        setMagicSent(false);
                        try {
                          // Store intended redirect
                          localStorage.setItem('authRedirectUrl', '/dashboard');
                          await supabase.auth.signInWithOtp({
                            email: magicEmail,
                            options: { emailRedirectTo: makeRedirectTo() }
                          });

                          // Only update state if this request wasn't invalidated
                          if (requestId === magicLinkRequestIdRef.current) {
                            setMagicSent(true);
                            shadcnToast({ description: "Check your email for the magic link!" });
                          }
                        } catch (error: any) {
                          // Only update state if this request wasn't invalidated
                          if (requestId === magicLinkRequestIdRef.current) {
                            const errorMsg = error.message || "Failed to send magic link";
                            setMagicError(errorMsg);
                            shadcnToast({ description: errorMsg, variant: "destructive" });
                          }
                        } finally {
                          // Only update state if this request wasn't invalidated
                          if (requestId === magicLinkRequestIdRef.current) {
                            setMagicLoading(false);
                          }
                        }
                      }}
                    >
                      <motion.div 
                        className={`relative ${focusedInput === "magic-email" ? 'z-10' : ''}`}
                        whileHover={{ scale: 1.01 }}
                        transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      >
                        <div className="absolute -inset-[0.5px] bg-gradient-to-r from-white/10 via-white/5 to-white/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300" />
                        
                        <div className="relative flex items-center overflow-hidden rounded-lg">
                          <Mail className={`absolute left-3 w-4 h-4 transition-all duration-300 ${
                            focusedInput === "magic-email" ? 'text-white' : 'text-white/40'
                          }`} />
                          
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            value={magicEmail}
                            onChange={e => {
                              setMagicEmail(e.target.value);
                              // Reset feedback states when user modifies email
                              if (magicSent) setMagicSent(false);
                              if (magicError) setMagicError(null);
                            }}
                            onFocus={() => setFocusedInput("magic-email")}
                            onBlur={() => setFocusedInput(null)}
                            className="w-full bg-white/5 border-transparent focus:border-white/20 text-white placeholder:text-white/30 h-10 transition-all duration-300 pl-10 pr-3 focus:bg-white/10"
                            required
                          />
                          
                          {/* Input highlight effect */}
                          {focusedInput === "magic-email" && (
                            <motion.div 
                              layoutId="input-highlight"
                              className="absolute inset-0 bg-white/5 -z-10"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            />
                          )}
                        </div>
                      </motion.div>
                      <button
                        type="submit"
                        className="w-full bg-figgy-500 hover:bg-figgy-600 text-white font-medium h-10 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                        disabled={magicLoading || magicSent}
                      >
                        {magicLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : magicSent ? '✓ Sent!' : 'Send Magic Link'}
                      </button>

                      {/* Success feedback */}
                      {magicSent && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-center"
                        >
                          Magic link sent! Check your email inbox (and spam folder).
                        </motion.div>
                      )}

                      {/* Error feedback */}
                      {magicError && !magicLoading && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-center"
                        >
                          {magicError}
                        </motion.div>
                      )}
                    </form>
                  )}
                </div>

                {/* Sign up link - hidden when embedded */}
                {!embedded && (
                  <motion.p
                    className="text-center text-xs text-white/60 mt-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    Don't have an account?{' '}
                    <Link
                      to="/magic-onboarding-v2"
                      className="relative inline-block group/signup"
                    >
                      <span className="relative z-10 text-white group-hover/signup:text-white/70 transition-colors duration-300 font-medium">
                        Sign up
                      </span>
                      <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white group-hover/signup:w-full transition-all duration-300" />
                    </Link>
                  </motion.p>
                )}

                {/* Legal links - hidden when embedded */}
                {!embedded && (
                  <motion.p
                    className="text-center text-xs text-white/40 mt-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    By continuing you agree to our{' '}
                    <Link to="/legal/terms-and-conditions" className="underline hover:text-white/60 transition-colors">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/legal/privacy-policy" className="underline hover:text-white/60 transition-colors">Privacy Policy</Link>
                  </motion.p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
